import {
  CommonCollapseButton,
  CommonHeading,
  CommonNewButton,
  CommonSearch,
  customUseEffect,
  formatDateValue,
  getCreatedByCreatedOnUpdatedByUpdatedOnColumn,
  Grid,
  MuiDataGrid,
  TabHeaderLayout,
  useMemo,
} from "@common";
import {
  activityRowSelect,
  getActivityApi,
} from "@features";
import { dateTimeFormat } from "@pionglobal/pionsharedui";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ActivityView from "./ActivityView";
import AddPage from "./AddPage";

const sanitizeId = (id) => {
  if (id === true || id === false || id === "true" || id === "false" || id === undefined || id === null) {
    return null;
  }
  return id;
};

const getSelectedId = (data, fallbackId) => {
  const cleanFallback = sanitizeId(fallbackId);
  if (Array.isArray(data)) {
    return sanitizeId(data[0]?.id || data[0]?._id) || cleanFallback || null;
  }
  return sanitizeId(data?.id || data?._id) || cleanFallback || null;
};

const ActivityPage = ({
  forCampaign,
  forProspect,
  forInquiry,
  forLead,
  forOpportunity,
  forAccount,
  forContact,
  forContract,
  forOrder,
  forCustomer,
  title = "Activity",
  permissions = {},
  ...props
}) => {
  const dispatch = useDispatch();
  const [showAdd, setShowAdd] = useState(false);
  const [viewMore, setViewMore] = useState(true);

  const gridRow = useSelector((state) => state?.activity?.data) || [];
  const selectedActivity = useSelector((state) => state?.activity?.selectedData);
  const selectedActivityId = getSelectedId(selectedActivity);

  // Entity Selected Data resolution from Redux state
  const campaignSelectedData = useSelector((state) => state?.campaign?.selectedData);
  const prospectSelectedData = useSelector((state) => state?.prospect?.selectedData);
  const inquirySelectedData = useSelector((state) => state?.inquiry?.selectedData);
  const leadSelectedData = useSelector((state) => state?.lead?.selectedData);
  const opportunitySelectedData = useSelector((state) => state?.opportunity?.selectedData);
  const accountSelectedData = useSelector((state) => state?.account?.selectedData);
  const contactSelectedData = useSelector((state) => state?.contact?.selectedData);
  const contractSelectedData = useSelector((state) => state?.contract?.selectedData);
  const orderSelectedData = useSelector((state) => state?.order?.selectedData);
  const customerSelectedData = useSelector((state) => state?.customer?.selectedData);

  const propsWithFlags = {
    forCampaign,
    forProspect,
    forInquiry,
    forLead,
    forOpportunity,
    forAccount,
    forContact,
    forContract,
    forOrder,
    forCustomer,
    campaignId: props?.campaignId,
    prospectId: props?.prospectId,
    inquiryId: props?.inquiryId,
    leadId: props?.leadId,
    opportunityId: props?.opportunityId,
    customerAccountId: props?.customerAccountId,
    contactId: props?.contactId,
    contractId: props?.contractId,
    orderId: props?.orderId,
    customerId: props?.customerId,
    ...props,
  };

  const api = useMemo(
    () => getActivityApi(propsWithFlags),
    [
      forCampaign,
      forProspect,
      forInquiry,
      forLead,
      forOpportunity,
      forAccount,
      forContact,
      forContract,
      forOrder,
      forCustomer,
      props?.campaignId,
      props?.prospectId,
      props?.inquiryId,
      props?.leadId,
      props?.opportunityId,
      props?.customerAccountId,
      props?.contactId,
      props?.contractId,
      props?.orderId,
      props?.customerId,
      props?.entityType,
    ]
  );

  const activeEntityId = useMemo(() => {
    const rawPropId = sanitizeId(propsWithFlags?.[api.idKey]);
    if (rawPropId && (typeof rawPropId === "string" || typeof rawPropId === "number")) {
      return rawPropId;
    }
    let resolved = null;
    switch (api.entityType) {
      case "contract":
        resolved = sanitizeId(props?.contractId) || getSelectedId(contractSelectedData);
        break;
      case "customer-account":
        resolved = sanitizeId(props?.customerAccountId) || getSelectedId(accountSelectedData);
        break;
      case "contact":
        resolved = sanitizeId(props?.contactId) || getSelectedId(contactSelectedData);
        break;
      case "opportunity":
        resolved = sanitizeId(props?.opportunityId) || getSelectedId(opportunitySelectedData);
        break;
      case "lead":
        resolved = sanitizeId(props?.leadId) || getSelectedId(leadSelectedData);
        break;
      case "campaign":
        resolved = sanitizeId(props?.campaignId) || getSelectedId(campaignSelectedData);
        break;
      case "inquiry":
        resolved = sanitizeId(props?.inquiryId) || getSelectedId(inquirySelectedData);
        break;
      case "prospect":
        resolved = sanitizeId(props?.prospectId) || getSelectedId(prospectSelectedData);
        break;
      case "customer":
        resolved = sanitizeId(props?.customerId) || getSelectedId(customerSelectedData);
        break;
      case "order":
        resolved = sanitizeId(props?.orderId) || getSelectedId(orderSelectedData);
        break;
      default:
        resolved = null;
    }
    return sanitizeId(resolved);
  }, [
    api.entityType,
    api.idKey,
    props,
    propsWithFlags,
    contractSelectedData,
    accountSelectedData,
    contactSelectedData,
    opportunitySelectedData,
    leadSelectedData,
    campaignSelectedData,
    inquirySelectedData,
    prospectSelectedData,
    customerSelectedData,
    orderSelectedData,
  ]);

  const originalRows = gridRow || [];
  const [filteredRows, setFilteredRows] = useState(originalRows);

  customUseEffect(() => {
    setFilteredRows(originalRows);
  }, [originalRows]);

  const getFunction = useCallback(() => {
    const cleanEntityId = sanitizeId(activeEntityId);
    if (cleanEntityId) {
      dispatch(api.get({ [api.idKey]: cleanEntityId }));
    } else {
      dispatch(api.get());
    }
  }, [dispatch, api, activeEntityId]);

  customUseEffect(() => {
    setViewMore(true);
    getFunction();
    return () => dispatch(activityRowSelect([]));
  }, [activeEntityId, api.entityType, getFunction, dispatch]);

  const data = useMemo(
    () => ({
      rows: filteredRows || [],
      columns: [
        {
          field: "activityType",
          headerName: "Activity Type",
          renderHeader: () => <strong>Activity Type</strong>,
          width: 180,
        },
        {
          field: "activityName",
          headerName: "Activity Name",
          renderHeader: () => <strong>Activity Name</strong>,
          width: 200,
        },
        {
          field: "assignedTo",
          headerName: "Assigned To",
          renderHeader: () => <strong>Assigned To</strong>,
          valueGetter: (params) => {
            if (typeof params === "object" && params) {
              return params?.userName || params?.email || params?.firstName || "";
            }
            return params || "";
          },
          width: 200,
        },
        {
          field: "dueDate",
          headerName: "Due Date",
          renderHeader: () => <strong>Due Date</strong>,
          valueGetter: (value, row) => row?.dueDate,
          valueFormatter: (value) => {
            if (!value) return "";
            try {
              return dateTimeFormat(value) || formatDateValue(value);
            } catch {
              return formatDateValue(value);
            }
          },
          sortComparator: (v1, v2) => new Date(v1) - new Date(v2),
          width: 180,
        },
        {
          field: "actualDate",
          headerName: "Actual Date",
          renderHeader: () => <strong>Actual Date</strong>,
          valueGetter: (value, row) => row?.actualDate,
          valueFormatter: (value) => {
            if (!value) return "";
            try {
              return dateTimeFormat(value) || formatDateValue(value);
            } catch {
              return formatDateValue(value);
            }
          },
          sortComparator: (v1, v2) => new Date(v1) - new Date(v2),
          width: 180,
        },
        {
          field: "priority",
          headerName: "Priority",
          renderHeader: () => <strong>Priority</strong>,
          valueGetter: (value, row) => {
            if (typeof row?.priority === "object" && row?.priority) {
              return row?.priority?.priorityType || row?.priority?.name || "";
            }
            return row?.priority || "";
          },
          width: 140,
        },
        {
          field: "description",
          headerName: "Description",
          renderHeader: () => <strong>Description</strong>,
          width: 220,
        },
        {
          field: "outComeNote",
          headerName: "Outcome Note",
          renderHeader: () => <strong>Outcome Note</strong>,
          width: 200,
        },
        ...getCreatedByCreatedOnUpdatedByUpdatedOnColumn(),
      ],
    }),
    [filteredRows]
  );

  const showAddFunction = () => setShowAdd(true);

  return (
    <div>
      <TabHeaderLayout>
        <Grid
          size={{ md: 2.5, lg: 2.5, xs: 2.5, xl: 2.5, sm: 2.5 }}
          style={{ display: "flex", justifyContent: "left" }}
        >
          <CommonCollapseButton viewMore={viewMore} setViewMore={setViewMore} />
          {permissions?.create && (
            <CommonNewButton handleNew={showAddFunction} />
          )}
        </Grid>
        <Grid
          size={{ md: 6, lg: 6, xs: 6, xl: 6, sm: 6 }}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <CommonHeading heading={title} />
        </Grid>
        <Grid
          size={{ md: 3.3, lg: 3.3, xs: 3.5, xl: 3.3, sm: 3.5 }}
          style={{ display: "flex", justifyContent: "right" }}
        >
          <CommonSearch
            rows={originalRows}
            searchFields={["activityType", "activityName", "description", "status", "dueDate", "actualDate", "priority"]}
            onFilter={setFilteredRows}
          />
        </Grid>
      </TabHeaderLayout>

      <Grid container spacing={0}>
        {viewMore && (
          <Grid size={{ sm: 12, md: selectedActivityId ? 6 : 12 }}>
            <MuiDataGrid
              data={data}
              select={(selectedRows) => {
                permissions?.details && dispatch(activityRowSelect(selectedRows));
              }}
            />
          </Grid>
        )}

        {viewMore && selectedActivityId && (
          <Grid size={{ sm: 12, md: 6 }}>
            <ActivityView
              heading={title}
              activityId={selectedActivityId}
              forCampaign={forCampaign}
              forProspect={forProspect}
              forInquiry={forInquiry}
              forLead={forLead}
              forOpportunity={forOpportunity}
              forAccount={forAccount}
              forContact={forContact}
              forContract={forContract}
              forOrder={forOrder}
              forCustomer={forCustomer}
              entityType={api.entityType}
              {...(sanitizeId(activeEntityId) ? { [api.idKey]: sanitizeId(activeEntityId) } : {})}
              getFunction={getFunction}
              permissions={permissions}
            />
          </Grid>
        )}
      </Grid>

      {showAdd && (
        <AddPage
          showAdd={showAdd}
          close={setShowAdd}
          getFunction={getFunction}
          forCampaign={forCampaign}
          forProspect={forProspect}
          forInquiry={forInquiry}
          forLead={forLead}
          forOpportunity={forOpportunity}
          forAccount={forAccount}
          forContact={forContact}
          forContract={forContract}
          forOrder={forOrder}
          forCustomer={forCustomer}
          entityType={api.entityType}
          {...(sanitizeId(activeEntityId) ? { [api.idKey]: sanitizeId(activeEntityId) } : {})}
        />
      )}
    </div>
  );
};

export default ActivityPage;
